import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { authService } from '../../services/authService';
import { format, parse, startOfWeek, getDay, startOfMonth, endOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './calendario.css';
import { collection, query, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Icons } from '../../utils/icons';
import { expandirRecurrencia } from './utils/recurrencia';
import { OFFICE_USERS } from './utils/officeUsers';
import { FESTIVOS } from './utils/festivos';
import EventoModal from './EventoModal';
import EventoDetalle from './EventoDetalle';
import TareasView from './TareasView';

const locales = { es };
const localizer = dateFnsLocalizer({
  format, parse,
  startOfWeek: (date) => startOfWeek(date, { locale: es }),
  getDay, locales,
});

const MESSAGES = {
  today: 'Hoy', previous: 'Anterior', next: 'Siguiente',
  month: 'Mes', week: 'Semana', day: 'Día', agenda: 'Agenda',
  date: 'Fecha', time: 'Hora', event: 'Evento',
  noEventsInRange: 'No hay eventos en este rango.',
  showMore: (total) => `+ ${total} más`,
};

function fechaISO(date) { return format(date, 'yyyy-MM-dd'); }
function horaISO(date)  { return format(date, 'HH:mm'); }

const COLOR_POR_TIPO = { tarea: '#F59E0B', visita: '#2563EB', reunion: '#DC2626' };

// Traduce una ocurrencia (evento normal o expandido de una serie) a los
// datos que espera EventoModal para editarla — usado tanto al editar desde
// el detalle del calendario como al pulsar una tarea en TareasView.
function ocurrenciaAModalInit(oc) {
  return {
    maestroId: oc.eventoId,
    fechaOriginal: oc.fechaOriginal || null,
    esRecurrente: !!(oc.recurrencia && oc.recurrencia.frecuencia !== 'ninguna'),
    tipo: oc.tipo, titulo: oc.titulo, descripcion: oc.descripcion,
    ubicacion: oc.ubicacion, color: oc.color, tags: oc.tags || [],
    fechaInicio: oc.fechaInicio, horaInicio: oc.horaInicio || '',
    fechaFin: oc.fechaFin, horaFin: oc.horaFin || '',
    todoElDia: oc.todoElDia, fechaVencimiento: oc.fechaVencimiento,
    participanteEmails: (oc.participantes || []).map(p => p.userId).filter(e => e !== oc.creadorId),
    recurrencia: oc.recurrencia, recordatorioMinutos: oc.recordatorioMinutos,
    clienteId: oc.clienteId, clienteNombre: oc.clienteNombre, clienteTelefono: oc.clienteTelefono,
  };
}

export default function CalendarioView() {
  const { user } = useAuth();
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [rango, setRango] = useState(() => ({ start: startOfMonth(new Date()), end: endOfMonth(new Date()) }));
  const [modalInit, setModalInit] = useState(null); // datos para crear/editar
  const [showModal, setShowModal] = useState(false);
  const [detalle, setDetalle] = useState(null); // ocurrencia seleccionada
  const [ausencias, setAusencias] = useState([]); // vacaciones/bajas del equipo de oficina (desde Fichajes)

  useEffect(() => {
    // Válvula de seguridad: los eventos recurrentes se expanden en cliente y
    // no tienen un campo de fecha único por el que filtrar sin perder series
    // antiguas que siguen vigentes. Para una agenda de oficina 3000 es mucho.
    const unsub = onSnapshot(
      query(collection(db, 'eventos'), limit(3000)),
      (snap) => { setRaw(snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))); setLoading(false); },
      (err) => { console.error('[onSnapshot eventos]', err); setLoading(false); }
    );
    return () => unsub();
  }, []);

  // Ausencias del equipo de oficina — se registran en Fichajes, aquí solo se
  // leen para avisar en el calendario. Sin filtro de mes: el endpoint ya
  // devuelve desde hace 6 meses en adelante (incluye todo lo futuro), así
  // cubre cualquier fecha que se navegue o se elija al crear un evento.
  useEffect(() => {
    const nombresOficina = new Set(OFFICE_USERS.map(u => u.nombre));
    fetch('/.netlify/functions/manageAusencias', { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(d => { if (d.success) setAusencias(d.ausencias.filter(a => nombresOficina.has(a.tecnicoNombre))); })
      .catch(() => {});
  }, []);

  const handleRangeChange = useCallback((range) => {
    if (Array.isArray(range)) setRango({ start: range[0], end: range[range.length - 1] });
    else if (range?.start && range?.end) setRango({ start: range.start, end: range.end });
  }, []);

  const ocurrencias = useMemo(() => {
    if (!rango) return [];
    const maestros = raw.filter(e => !e.seriePadreId);
    const excepcionesPorMaestro = new Map();
    for (const e of raw.filter(e => e.seriePadreId)) {
      if (!excepcionesPorMaestro.has(e.seriePadreId)) excepcionesPorMaestro.set(e.seriePadreId, []);
      excepcionesPorMaestro.get(e.seriePadreId).push(e);
    }
    const rangoInicio = fechaISO(rango.start);
    const rangoFin = fechaISO(rango.end);
    const out = [];
    for (const m of maestros) {
      const excs = excepcionesPorMaestro.get(m.id) || [];
      out.push(...expandirRecurrencia(m, excs, rangoInicio, rangoFin));
    }
    return out;
  }, [raw, rango]);

  const rbcEvents = useMemo(() => ocurrencias.map(oc => {
    const start = oc.todoElDia
      ? new Date(`${oc.fechaInicio}T00:00:00`)
      : new Date(`${oc.fechaInicio}T${oc.horaInicio || '00:00'}:00`);
    const end = oc.todoElDia
      ? new Date(`${oc.fechaFin || oc.fechaInicio}T23:59:59`)
      : new Date(`${oc.fechaFin || oc.fechaInicio}T${oc.horaFin || oc.horaInicio || '00:00'}:00`);
    return { title: oc.titulo, start, end, allDay: !!oc.todoElDia, resource: oc };
  }), [ocurrencias]);

  // Festivos y ausencias del equipo, mostrados como eventos de solo lectura
  // (no se pueden editar ni borrar desde aquí) para saber de un vistazo
  // quién no está trabajando ese día.
  const eventosVirtuales = useMemo(() => {
    if (!rango) return [];
    const out = [];
    for (let d = new Date(rango.start); d <= rango.end; d = new Date(d.getTime() + 86400000)) {
      const iso = fechaISO(d);
      const nombreFestivo = FESTIVOS[iso];
      if (nombreFestivo) {
        out.push({
          title: `🎉 ${nombreFestivo}`,
          start: new Date(`${iso}T00:00:00`), end: new Date(`${iso}T23:59:59`), allDay: true,
          resource: { esVirtual: true, tipoVirtual: 'festivo' },
        });
      }
    }
    for (const a of ausencias) {
      const rangoInicio = fechaISO(rango.start), rangoFin = fechaISO(rango.end);
      if (a.fechaFin < rangoInicio || a.fechaInicio > rangoFin) continue; // fuera del rango visible
      out.push({
        title: `🌴 ${a.tecnicoNombre} — ${a.tipoLabel}`,
        start: new Date(`${a.fechaInicio}T00:00:00`), end: new Date(`${a.fechaFin}T23:59:59`), allDay: true,
        resource: { esVirtual: true, tipoVirtual: 'ausencia', ausencia: a },
      });
    }
    return out;
  }, [rango, ausencias]);

  const eventosCalendario = useMemo(() => [...rbcEvents, ...eventosVirtuales], [rbcEvents, eventosVirtuales]);

  const eventPropGetter = useCallback((event) => {
    if (event.resource?.esVirtual) {
      const color = event.resource.tipoVirtual === 'festivo' ? '#6B7280' : '#7C3AED';
      return { style: { backgroundColor: color, borderColor: color, opacity: 0.85, cursor: 'default' } };
    }
    const color = event.resource.color || COLOR_POR_TIPO[event.resource.tipo] || COLOR_POR_TIPO.reunion;
    return { style: { backgroundColor: color, borderColor: color } };
  }, []);

  const dayPropGetter = useCallback((date) => {
    return FESTIVOS[fechaISO(date)] ? { className: 'rbc-festivo-cell' } : {};
  }, []);

  const handleSelectSlot = ({ start, end, action }) => {
    setModalInit({
      fechaInicio: fechaISO(start),
      horaInicio: action === 'select' || view === 'month' ? '' : horaISO(start),
      fechaFin: fechaISO(end),
      horaFin: action === 'select' || view === 'month' ? '' : horaISO(end),
      todoElDia: view === 'month',
    });
    setShowModal(true);
  };

  const handleSelectEvent = (event) => {
    if (event.resource?.esVirtual) return;
    setDetalle(event.resource);
  };

  const handleNuevoEvento = () => {
    setModalInit({ fechaInicio: fechaISO(new Date()), horaInicio: '', fechaFin: fechaISO(new Date()), horaFin: '', todoElDia: false });
    setShowModal(true);
  };

  const handleNuevaTarea = () => {
    setModalInit({ tipo: 'tarea', fechaInicio: fechaISO(new Date()), fechaFin: fechaISO(new Date()) });
    setShowModal(true);
  };

  const handleEditarOcurrencia = (oc) => {
    setModalInit(ocurrenciaAModalInit(oc));
    setDetalle(null);
    setShowModal(true);
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl shadow-lg border p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2 mb-1">
            <Icons.Calendar className="w-6 h-6 text-red-600" />
            Calendario
          </h2>
          <p className="text-gray-500 text-sm">Reuniones, tareas y visitas del equipo</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleNuevoEvento}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors">
            <Icons.Plus className="w-4 h-4" />Nuevo evento
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-start">
        <div className="lg:col-span-3 bg-white rounded-2xl shadow-lg border p-4">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-gray-400 gap-3">
              <div className="w-5 h-5 border-2 border-gray-300 border-t-red-500 rounded-full animate-spin" />
              Cargando calendario...
            </div>
          ) : (
            <div style={{ height: 700 }} className="calendario-zapata">
              <Calendar
                localizer={localizer}
                culture="es"
                messages={MESSAGES}
                events={eventosCalendario}
                date={date}
                view={view}
                onNavigate={setDate}
                onView={setView}
                onRangeChange={handleRangeChange}
                views={['month', 'week', 'day', 'agenda']}
                selectable
                onSelectSlot={handleSelectSlot}
                onSelectEvent={handleSelectEvent}
                eventPropGetter={eventPropGetter}
                dayPropGetter={dayPropGetter}
                popup
              />
            </div>
          )}
        </div>

        <div className="lg:col-span-1 lg:max-h-[700px] lg:overflow-y-auto">
          <TareasView raw={raw} usuario={user} onEditar={handleEditarOcurrencia} onNuevaTarea={handleNuevaTarea} />
        </div>
      </div>

      {showModal && (
        <EventoModal
          init={modalInit}
          creador={user}
          raw={raw}
          ausencias={ausencias}
          onClose={() => setShowModal(false)}
          onSaved={() => setShowModal(false)}
        />
      )}

      {detalle && (
        <EventoDetalle
          ocurrencia={detalle}
          usuario={user}
          onClose={() => setDetalle(null)}
          onEditar={() => handleEditarOcurrencia(detalle)}
        />
      )}
    </div>
  );
}
